import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { ApiService } from 'src/common/api-service/API-BETTING'
import { consoleLogAPI } from 'src/common/functions'

export const useBettingStore = defineStore('betting', () => {
  const MAX_COEF = 2000
  const trimCoefFunc = ref((coef) => {
    return coef
  })
  const sports = ref(null)
  const videoEventsIds = ref(null)
  const liveMatchTrackerBroadcastId = ref(null)
  const liveTournamentTableId = ref(null)
  const liveVideoSrc = ref(null)
  const liveVideoName = ref(null)
  const history = ref({
    bet_list: [],
  })
  const basket = ref({})
  const liveSports = ref([])
  const favorites = ref([])
  const snackbarMessage = ref('')
  const sideRightMenuIsExpand = ref(false)
  const liveMatchTrackerShow = ref(false)
  const liveTournamentTableShow = ref(false)
  const liveVideoShow = ref(false)
  const activePromoChip = ref('all')
  const fastbetOnProgress = ref(false)
  const fastbetSum = ref(100)
  const betsAccepted = ref(false)
  const historyTimeShift = ref(null)
  const selectedPromoFilters = ref([])

  const getLiveSports = computed(() => Object.values(liveSports.value))
  const getBasket = computed(() => basket.value ?? null)
  const getFavorites = computed(() => {
    const result = []
    const categories = [...new Set(favorites.value?.map((event) => event.status))]

    categories.forEach((category) => {
      if (category !== 'RESULTS') {
        const events = favorites.value.filter((event) => event.status === category).map(({ ...eventData }) => eventData)
        result.push({ category, events })
      }
    })

    return result.sort((a, b) => (b.category > a.category ? 1 : -1))
  })
  const getExpressBonusPercent = computed(() => {
    if (basket.value?.express_bonus_rate > 1) {
      return Math.round((basket.value.express_bonus_rate - 1) * 100)
    }
    return 0
  })
  const getTotalCoefficient = computed(() => {
    let expressRate = basket.value?.express_rate
    if (basket.value?.express_bonus_rate > 1) {
      expressRate = Math.floor(basket.value.express_rate * basket.value.express_bonus_rate * 100) / 100
    }

    return Math.min(expressRate, MAX_COEF).toFixed(2)
  })

  function updateTrimCoefFunc(data) {
    trimCoefFunc.value = function (
      value, // original koef
      percent = data.v1, // v1
      correctKoef = data.v2, // v2
      softCorrectKoef = data.v3, // v3
      doubleCorrectKoef = data.v4, // v4
    ) {
      let newValue = value

      const normalize = () => {
        if (newValue < 1.01) {
          newValue = '1.01'
        } else {
          newValue = newValue.toFixed(2)
        }
      }

      const getValue = () => {
        const lastSymbol = newValue.substr(-1)

        if (
          lastSymbol !== '5' &&
          lastSymbol !== '0' &&
          newValue.indexOf('.') + 3 === newValue.length &&
          newValue >= 1.1
        ) {
          newValue = `${newValue.slice(0, -1)}${newValue.substr(-1) > 5 ? '5' : ''}`
        }
      }

      if (percent && percent > 0) {
        newValue = (newValue * percent) / 1000

        normalize()
      } else if (softCorrectKoef && !correctKoef) {
        newValue -= newValue * 0.03

        normalize()
        getValue()
      } else if (correctKoef) {
        newValue -= (newValue - 1) * 0.3

        normalize()

        if (doubleCorrectKoef) {
          newValue -= (newValue - 1) * 0.5

          normalize()
        }

        getValue()
      } else {
        newValue = parseFloat(newValue).toFixed(2)
      }

      return newValue
    }
  }

  async function fetchHistory({ date, date_end, filter, offset, time_shift }) {
    await ApiService.history({ date, date_end, filter, limit: 10, offset, time_shift })
      .then(({ data }) => {
        history.value = data
      })
      .catch((error) => {
        console.error(error)
      })
  }

  async function fetchSports(payload) {
    const { data } = await ApiService.slice({ live: '1', ...payload })

    liveSports.value = data.reduce((aggregatedData, sport) => {
      const tournament = { id: sport.id, name: sport.cn }
      const events = sport.it.map((event) => ({ ...event, tournament }))
      events.forEach((event) => {
        if (event.it) {
          event.it.forEach((market) => {
            market.v = trimCoefFunc.value(market.v)
          })
        }
      })
      if (aggregatedData[sport.so]) {
        aggregatedData[sport.so].events.push(...events)
        aggregatedData[sport.so].tournaments.push(tournament)
      } else {
        aggregatedData[sport.so] = {
          id: sport.sport_id,
          name: sport.sn,
          order: sport.so,
          events,
          tournaments: [tournament],
        }
      }
      return aggregatedData
    }, {})
  }

  async function basketAdd({ coefs_ids, sport_id }) {
    try {
      const { data } = await ApiService.basketAdd({ coefs_ids, sport_id })
      await addBasketWithScores(data)
    } catch (error) {
      console.error(error)
      updateSnackbarMessage(error.err_desc)
      return error
    }
  }

  async function basketDelete({ coefs_ids }) {
    try {
      const { data } = await ApiService.basketDelete({ coefs_ids })
      await addBasketWithScores(data)
    } catch (error) {
      console.error(error)
    }
  }

  async function fetchBasket() {
    try {
      const { data } = await ApiService.basketList()
      await addBasketWithScores(data)
    } catch (error) {
      console.error(error)
    }
  }

  async function basketClear() {
    ApiService.basketClear()
      .then(({ data }) => {
        basket.value = data
      })
      .catch((error) => {
        console.error(error)
      })
  }

  function updateSnackbarMessage(payload) {
    snackbarMessage.value = payload
  }

  function basketSave({ sum, bet_type, save_any, any_handicap, bonus, system_type }) {
    return ApiService.basketSave({ sum, bet_type, save_any, any_handicap, bonus, system_type })
  }

  async function basketFast({ sum, sport_id, save_any, coefs_ids, fast = '1' }) {
    try {
      await ApiService.basketFast({ sum, sport_id, save_any, coefs_ids, fast })
    } catch (error) {
      console.error(error)
    }
  }

  async function fetchFavorites() {
    try {
      const { data } = await ApiService.favoritesList()
      favorites.value = data
    } catch (error) {
      favorites.value = []
      console.error('fetchFavorites error', error)
    }
  }

  async function addFavorite(event) {
    ApiService.favorites({
      action: 'add',
      sport_id: event.sport_id || event.sid,
      match_id: event.id,
    })
      .then(() => {
        this.fetchFavorites()
      })
      .catch((e) => {
        console.error(e)
      })
  }

  async function deleteFavorite(event) {
    ApiService.favorites({
      action: 'remove',
      sport_id: event.sport_id || event.sid,
      match_id: event.id,
    })
      .then(() => {
        this.fetchFavorites()
      })
      .catch((e) => {
        console.error(e)
      })
  }

  async function deleteAllFavorites() {
    await ApiService.favorites({ action: 'clearAll' })
      .then(() => {
        favorites.value = []
      })
      .catch((error) => {
        console.error(error)
      })
  }

  function openLiveMatchTracker(broadcastId) {
    liveMatchTrackerBroadcastId.value = broadcastId
    liveMatchTrackerShow.value = true
  }

  function openliveTournamentTable(broadcastId) {
    liveTournamentTableId.value = broadcastId
    liveTournamentTableShow.value = true
  }

  async function openLiveVideo(eventId, eventName, isMobile) {
    try {
      const { data } = await ApiService.video({ match_id: eventId, is_mobile: isMobile ? 1 : 0 })
      liveVideoSrc.value = data?.src
      liveVideoShow.value = true
      liveVideoName.value = eventName
    } catch (e) {
      consoleLogAPI(e)
    }
  }

  function updateSideRightMenuExpanded(payload) {
    sideRightMenuIsExpand.value = payload
  }

  function updateLiveMatchTrackerShow(payload) {
    liveMatchTrackerShow.value = payload
  }

  function updateTournamentTableShow(payload) {
    liveTournamentTableShow.value = payload
  }

  function updateLiveVideoShow(payload) {
    liveVideoShow.value = payload
  }

  function updateFastBetOnProgress(payload) {
    fastbetOnProgress.value = payload
  }

  function updateBetsAccepted(payload) {
    betsAccepted.value = payload
  }

  function updateHistoryTimeShift(payload) {
    historyTimeShift.value = payload
  }

  function updateActivePromoChip(payload) {
    activePromoChip.value = payload
  }

  function updateSelectedPromoFilters(payload) {
    selectedPromoFilters.value = payload
  }

  function updateFastBetSum(payload) {
    fastbetSum.value = payload
  }

  async function addBasketWithScores(data) {
    const stakesList = data.stakes_list || {}

    const matchIds = Object.values(stakesList).map((stake) => stake.matchid)

    if (matchIds.length === 0) {
      basket.value = data
      return
    }

    const sliceResponse = await ApiService.slice({
      live: '1',
      match_ids: JSON.stringify(matchIds),
    })

    if (!sliceResponse.data) {
      basket.value = data
      return
    }

    const matches = []
    sliceResponse.data.forEach((league) => {
      league.it.forEach((match) => {
        matches.push({
          id: match.id,
          sc: match.sc,
          scd: match.scd,
        })
      })
    })

    for (const stakeKey in stakesList) {
      const stake = stakesList[stakeKey]
      const match = matches.find((m) => m.id === stake.matchid)
      if (match) {
        stake.sc = match.sc
        stake.scd = match.scd
      }
    }

    basket.value = {
      ...data,
      stakes_list: { ...stakesList },
    }
  }

  return {
    MAX_COEF,
    // refs
    sports,
    videoEventsIds,
    liveMatchTrackerBroadcastId,
    liveTournamentTableId,
    liveVideoSrc,
    liveVideoName,
    history,
    basket,
    liveSports,
    favorites,
    snackbarMessage,
    sideRightMenuIsExpand,
    liveMatchTrackerShow,
    liveTournamentTableShow,
    liveVideoShow,
    activePromoChip,
    fastbetSum,
    fastbetOnProgress,
    betsAccepted,
    historyTimeShift,
    selectedPromoFilters,
    trimCoefFunc,

    // computed
    getLiveSports,
    getBasket,
    getFavorites,
    getExpressBonusPercent,
    getTotalCoefficient,

    //functions
    fetchHistory,
    fetchSports,
    basketAdd,
    basketDelete,
    fetchBasket,
    basketClear,
    basketSave,
    basketFast,
    fetchFavorites,
    addFavorite,
    deleteFavorite,
    deleteAllFavorites,
    openLiveMatchTracker,
    openliveTournamentTable,
    openLiveVideo,
    updateSideRightMenuExpanded,
    updateLiveMatchTrackerShow,
    updateTournamentTableShow,
    updateLiveVideoShow,
    updateSnackbarMessage,
    updateFastBetOnProgress,
    updateFastBetSum,
    updateBetsAccepted,
    updateHistoryTimeShift,
    updateActivePromoChip,
    updateSelectedPromoFilters,
    updateTrimCoefFunc,
  }
})
